import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { useOpsBrands } from "@/hooks/useOpsBrands";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Check, X, Pencil, Eye, Power, Trash2, Download } from "lucide-react";
import { istToday } from "@/lib/dateFormat";

const STATUS_COLORS = { draft: "bg-amber-100 text-amber-800", active: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-800", inactive: "bg-zinc-200 text-zinc-500" };
const fmtIST = (utc) => { if (!utc) return ""; try { const hasOffset = /[+-]\d{2}:\d{2}$/.test(utc) || utc.endsWith("Z"); const d = new Date(hasOffset ? utc : utc + "Z"); if (isNaN(d.getTime())) return ""; const ist = new Date(d.getTime() + 5.5 * 3600000); const dd = String(ist.getDate()).padStart(2, "0"); const mm = String(ist.getMonth() + 1).padStart(2, "0"); const hh = String(ist.getHours()).padStart(2, "0"); const mi = String(ist.getMinutes()).padStart(2, "0"); return `${dd}-${mm}-${ist.getFullYear()}, ${hh}:${mi} IST`; } catch { return ""; } };

const EMPTY_ITEM = {
  item_code: "", item_name: "", item_type: "", item_category: "", brand: "Skippi",
  manufacturing_model: "na", description: "",
  primary_uom: "pcs", alternate_uom: "", hsn_code: "", gst_pct: "",
  shelf_life_days: 0, reorder_level: 0, reorder_qty: 0, lead_time_days: 0,
  storage_condition: "ambient", batch_convention_note: "", notes: "",
  quality_params: [], pack_recipe: [],
  dispatch_pack_size: 0,
  freight_borne_by: "vendor", base_price: 0, estimated_freight: 0, actual_freight: 0,
};

function isAdmin(user) {
  return (user?.roles || []).some(r => ["Admin", "admin"].includes(r));
}

function hasViewCost(user) {
  const perms = user?.permissions || [];
  return perms.includes("item_master.view_cost") || perms.includes("admin.full_access") || isAdmin(user);
}

export default function OpsItemsPage() {
  const { getValues } = useConfig();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const canViewPricing = hasViewCost(user);
  const itemTypes = getValues("ops_item_types");
  const itemCategories = getValues("ops_item_categories");
  const brands = getValues("ops_brands");
  const { brandLinkOn, brandOptions } = useOpsBrands();   // FX1 C2
  const mfgModels = getValues("ops_manufacturing_models");
  const uomList = getValues("ops_uom_list");
  const storageConditions = getValues("ops_storage_conditions");
  const gstRates = getValues("gst_rates");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMfg, setFilterMfg] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_ITEM });
  const [editId, setEditId] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [deactivateItem, setDeactivateItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterType) params.item_type = filterType;
      if (filterCategory) params.item_category = filterCategory;
      if (filterStatus) params.status = filterStatus;
      if (filterMfg) params.mfg_model = filterMfg;
      if (showInactive && admin) params.show_inactive = "true";
      const { data } = await api.get("/ops/inventory/items", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [search, filterType, filterCategory, filterStatus, filterMfg, showInactive, admin]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    if (admin) {
      api.get("/ops/pending-counts").then(({ data }) => setPendingCount(data.item_master || 0)).catch(() => {});
    }
  }, [admin]);

  const openCreate = () => { setEditId(null); setFormData({ ...EMPTY_ITEM }); setFormDirty(false); setShowForm(true); };
  const openEdit = (item) => {
    setEditId(item._id);
    setFormData({
      ...item,
      batch_convention_note: item.batch_convention_note || item.batch_number_format || "",
      base_price: item.base_price_paise ? (item.base_price_paise / 100) : 0,
      estimated_freight: item.estimated_freight_paise ? (item.estimated_freight_paise / 100) : 0,
      actual_freight: item.actual_freight_paise ? (item.actual_freight_paise / 100) : 0,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/ops/inventory/items/${editId}`, formData);
        toast.success("Item updated");
      } else {
        await api.post("/ops/inventory/items", formData);
        toast.success("Item created as draft");
      }
      setShowForm(false);
      setFormDirty(false);
      fetchItems();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/ops/inventory/items/${id}/approve`);
      toast.success("Item approved & active");
      fetchItems();
      setViewItem(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return toast.error("Rejection reason required");
    try {
      await api.put(`/ops/inventory/items/${rejectId}/reject`, { reason: rejectReason });
      toast.success("Item rejected");
      setRejectId(null);
      setRejectReason("");
      fetchItems();
      setViewItem(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleDeactivate = async () => {
    if (!deactivateItem) return;
    try {
      await api.delete(`/ops/inventory/items/${deactivateItem._id}`);
      toast.success("Item deactivated");
      setDeactivateItem(null);
      fetchItems();
      setViewItem(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleReactivate = async (id) => {
    try {
      await api.put(`/ops/inventory/items/${id}/reactivate`);
      toast.success("Item reactivated");
      fetchItems();
      setViewItem(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const needsPackRecipe = ["SFG-Box", "SFG-Bag", "FG-Carton"].includes(formData.item_type);
  const showDispatch = ["SFG-Box", "SFG-Bag", "FG-Buyout"].includes(formData.item_type);
  const displayItemTypes = editId ? itemTypes : itemTypes.filter(t => t !== "FG-Carton");
  // 1F: Purchase pricing calculated values
  const calcPurchasePrice = () => {
    const bp = parseFloat(formData.base_price) || 0;
    const gst = parseFloat(formData.gst_pct) || 0;
    return +(bp * (1 + gst / 100)).toFixed(2);
  };
  const calcLandedCost = () => {
    const pp = calcPurchasePrice();
    if (formData.freight_borne_by === "skippi") return +(pp + (parseFloat(formData.estimated_freight) || 0)).toFixed(2);
    return pp;
  };

  return (
    <div className="space-y-4" data-testid="ops-items-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="ops-items-title">Item Master</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { const headers = ["Item Code","Item Name","Item Type","Item Category","MFG Model","Brand","Primary UOM","Alternate UOM","GST%","HSN Code",...(canViewPricing ? ["Base Price (Rs)","Purchase Price (Rs)","Estimated Freight (Rs)","Landed Cost (Rs)","Freight Borne By"] : []),"Reorder Level","Reorder Qty","Lead Time (days)","Shelf Life (days)","Storage Condition","Batch Convention","Description","Notes","Status","Created Date","Created By"]; const rows = items.map(i => [i.item_code||"",i.item_name||"",i.item_type||"",i.item_category||"",i.manufacturing_model||"",i.brand||"",i.primary_uom||"",i.alternate_uom||"",i.gst_pct||"",i.hsn_code||"",...(canViewPricing ? [(i.base_price_paise?(i.base_price_paise/100).toFixed(2):""),(i.purchase_price_paise?(i.purchase_price_paise/100).toFixed(2):""),(i.estimated_freight_paise?(i.estimated_freight_paise/100).toFixed(2):""),(i.landed_cost_paise?(i.landed_cost_paise/100).toFixed(2):""),i.freight_borne_by||""] : []),i.reorder_level||"",i.reorder_qty||"",i.lead_time_days||"",i.shelf_life_days||"",i.storage_condition||"",i.batch_convention_note||"",(i.description||"").replace(/\n/g," "),(i.notes||"").replace(/\n/g," "),i.status||"",(i.created_at||"").slice(0,10),i.created_by_name||""]); const csv = [headers,...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n"); const b = new Blob([csv],{type:"text/csv"}); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `item_master_${istToday()}.csv`; a.click(); }} data-testid="export-items-csv"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
          <Button onClick={openCreate} data-testid="create-item-btn"><Plus className="w-4 h-4 mr-1" />New Item</Button>
        </div>
      </div>

      {/* 1G: Pending approval banner */}
      {admin && pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center justify-between" data-testid="items-pending-banner">
          <span className="text-sm text-amber-800 font-medium">{pendingCount} item(s) awaiting your approval</span>
          <Button size="sm" variant="outline" onClick={() => setFilterStatus("draft")} className="text-amber-800 border-amber-300 hover:bg-amber-100">Review Now</Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end" data-testid="items-filter-bar">
        <div className="w-64">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
            <Input placeholder="Search code or name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" data-testid="items-search" />
          </div>
        </div>
        <Select value={filterType} onValueChange={v => setFilterType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="items-filter-type"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {itemTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={v => setFilterCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="items-filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {itemCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={v => setFilterStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36" data-testid="items-filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterMfg} onValueChange={v => setFilterMfg(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All MFG" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All MFG</SelectItem>{mfgModels.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        {admin && (
          <div className="flex items-center gap-2 ml-2" data-testid="show-inactive-toggle">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} id="show-inactive" />
            <Label htmlFor="show-inactive" className="text-xs text-zinc-500 cursor-pointer">Show Inactive</Label>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-auto" data-testid="items-table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="w-36">Item Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-28">Type</TableHead>
              <TableHead className="w-28">Category</TableHead>
              <TableHead className="w-28">MFG Model</TableHead>
              <TableHead className="w-20">UOM</TableHead>
              {canViewPricing && <TableHead className="w-36 text-right">Purchase Price</TableHead>}
              {canViewPricing && <TableHead className="w-36 text-right">Landed Cost</TableHead>}
              <TableHead className="w-20">GST %</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={canViewPricing ? 10 : 9} className="text-center py-8 text-zinc-400">Loading...</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={canViewPricing ? 10 : 9} className="text-center py-8 text-zinc-400">No items found</TableCell></TableRow>
            ) : items.map(item => (
              <TableRow key={item._id} className={item.is_deleted ? "opacity-50" : ""} data-testid={`item-row-${item.item_code}`}>
                <TableCell className="font-mono text-xs">{item.item_code}</TableCell>
                <TableCell className="font-medium">{item.item_name}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{item.item_type}</Badge></TableCell>
                <TableCell className="text-xs">{item.item_category || "—"}</TableCell>
                <TableCell className="text-xs">{item.manufacturing_model}</TableCell>
                <TableCell>{item.primary_uom}</TableCell>
                {canViewPricing && (
                  <TableCell className="text-right">
                    {(() => {
                      const pp = item.purchase_price_paise || 0;
                      const prev = item.prev_purchase_price_paise || 0;
                      if (pp === 0) return <span className="text-zinc-400 text-xs">—</span>;
                      const ppRs = (pp / 100).toFixed(2);
                      let arrow = null; let arrowColor = "";
                      if (prev > 0 && pp !== prev) { if (pp > prev) { arrow = "▲"; arrowColor = "text-red-500"; } else { arrow = "▼"; arrowColor = "text-green-600"; } }
                      return <span className="font-mono text-xs flex items-center justify-end gap-1">Rs {ppRs}<span className="text-[10px] text-zinc-400">/{item.primary_uom}</span>{arrow && <span className={`text-[10px] font-bold ${arrowColor}`} title={`Was Rs ${(prev/100).toFixed(2)}`}>{arrow}</span>}</span>;
                    })()}
                  </TableCell>
                )}
                {canViewPricing && (
                  <TableCell className="text-right">
                    {(() => {
                      const lc = item.landed_cost_paise || item.purchase_price_paise || 0;
                      if (lc === 0) return <span className="text-zinc-400 text-xs">—</span>;
                      return <span className="font-mono text-xs flex items-center justify-end gap-1">Rs {(lc / 100).toFixed(2)}<span className="text-[10px] text-zinc-400">/{item.primary_uom}</span></span>;
                    })()}
                  </TableCell>
                )}
                <TableCell>{item.gst_pct}%</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[item.status] || "bg-zinc-100"}`}>{item.is_deleted ? "inactive" : item.status}</span></TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="icon" variant="ghost" onClick={() => setViewItem(item)} data-testid={`view-item-${item.item_code}`}><Eye className="w-4 h-4" /></Button>
                  {(admin || (!item.is_deleted && (item.status === "draft" || item.status === "pending_approval"))) && (
                    <Button size="icon" variant="ghost" onClick={() => openEdit(item)} data-testid={`edit-item-${item.item_code}`}><Pencil className="w-4 h-4" /></Button>
                  )}
                  {admin && !item.is_deleted && (
                    <Button size="icon" variant="ghost" onClick={() => setDeactivateItem(item)} data-testid={`deactivate-item-${item.item_code}`}><Power className="w-4 h-4 text-red-400" /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-zinc-500" data-testid="items-count">Showing {items.length} item(s)</p>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v && formDirty && !window.confirm("You have unsaved changes. Close anyway? Your data will be lost.")) return; if (!v) { setFormDirty(false); setShowForm(false); } }}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="item-form-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Item" : "New Item (Draft)"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {/* 1A: Item Code */}
            <div className="col-span-2">
              <Label>Item Code *</Label>
              {editId ? (
                admin ? (
                  <Input value={formData.item_code} onChange={e => setFormData(p => ({ ...p, item_code: e.target.value.toUpperCase().replace(/\s/g, "") }))} maxLength={20} data-testid="item-code-input" />
                ) : (
                  <div className="font-mono text-sm font-semibold mt-1" data-testid="item-code-readonly">{formData.item_code}</div>
                )
              ) : (
                <>
                  <Input value={formData.item_code} onChange={e => setFormData(p => ({ ...p, item_code: e.target.value.toUpperCase().replace(/\s/g, "") }))} maxLength={20} data-testid="item-code-input" />
                  <p className="text-[10px] text-zinc-400 mt-1">Max 20 chars. Alphanumeric + special chars. No spaces. CAPS only. Cannot be changed after creation.</p>
                  {["SFG-Pop", "SFG-Box", "SFG-Bag"].includes(formData.item_type) && <p className="text-[10px] text-blue-500 mt-0.5">This Item Code becomes your internal SKU reference. For commercial box/bag SKUs, use the same code that Shruthi will create in OMS Catalogue so they stay in sync. Example: BLUBX-12MIX for Blue Box 12-Pack.</p>}
                </>
              )}
            </div>
            <div className="col-span-2">
              <Label>Item Name *</Label>
              <Input value={formData.item_name} onChange={e => setFormData(p => ({ ...p, item_name: e.target.value }))} spellCheck={true} autoCorrect="on" autoCapitalize="words" data-testid="item-name-input" />
            </div>
            <div>
              <Label>Item Type *</Label>
              <Select value={formData.item_type} onValueChange={v => {
                if (["PM", "Consumable", "Spare-Part"].includes(v)) {
                  setFormData(p => ({ ...p, item_type: v, manufacturing_model: "na" }));
                } else {
                  setFormData(p => ({ ...p, item_type: v }));
                }
              }}>
                <SelectTrigger data-testid="item-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{displayItemTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              {!editId && <p className="text-[10px] text-zinc-400 mt-1">Cartons are dispatch grouping units — use Dispatch Pack Size on SFG-Box/Bag items instead.</p>}
            </div>
            {/* 1B: Item Category */}
            <div>
              <Label>Item Category</Label>
              <Select value={formData.item_category || "_none"} onValueChange={v => setFormData(p => ({ ...p, item_category: v === "_none" ? "" : v }))}>
                <SelectTrigger data-testid="item-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">— None —</SelectItem>{itemCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {/* 1H: Brand */}
            <div>
              <Label>Brand</Label>
              <Select
                value={brandLinkOn ? (formData.brand_id || "") : (formData.brand || "Skippi")}
                onValueChange={v => setFormData(p => brandLinkOn
                  ? ({ ...p, brand_id: v, brand: (brandOptions.find(b => b.id === v) || {}).name || "" })
                  : ({ ...p, brand: v }))}
              >
                <SelectTrigger data-testid="item-brand-select"><SelectValue placeholder={brandLinkOn ? "Choose an active brand" : undefined} /></SelectTrigger>
                <SelectContent data-testid="item-brand-options">
                  {brandOptions.map(b => (
                    <SelectItem key={b.id || b.name} value={brandLinkOn ? b.id : b.name}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* 1C: Manufacturing Model */}
            <div>
              <Label>Manufacturing Model</Label>
              <Select value={formData.manufacturing_model} onValueChange={v => setFormData(p => ({ ...p, manufacturing_model: v }))}>
                <SelectTrigger data-testid="item-mfg-select"><SelectValue /></SelectTrigger>
                <SelectContent>{mfgModels.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Primary UOM</Label>
              <Select value={formData.primary_uom} onValueChange={v => setFormData(p => ({ ...p, primary_uom: v }))}>
                <SelectTrigger data-testid="item-uom-select"><SelectValue /></SelectTrigger>
                <SelectContent>{uomList.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[10px] text-zinc-400 mt-0.5">The unit used for pricing and BOM entry. All purchase prices and BOM quantities are in this unit.</p>
            </div>
            <div>
              <Label>Alternate UOM</Label>
              <Select value={formData.alternate_uom || "_none"} onValueChange={v => setFormData(p => ({ ...p, alternate_uom: v === "_none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">None</SelectItem>{uomList.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[10px] text-zinc-400 mt-0.5">Optional. A secondary unit for reference only. Leave blank if not needed.</p>
            </div>
            {/* 1D: HSN + GST mandatory */}
            <div>
              <Label>HSN Code *</Label>
              <Input value={formData.hsn_code} onChange={e => setFormData(p => ({ ...p, hsn_code: e.target.value }))} data-testid="item-hsn-input" />
              <p className="text-[10px] text-zinc-400 mt-1">Enter 4, 6, or 8 digit HSN code</p>
            </div>
            <div>
              <Label>GST % *</Label>
              <Select value={String(formData.gst_pct)} onValueChange={v => setFormData(p => ({ ...p, gst_pct: parseFloat(v) || 0 }))}>
                <SelectTrigger data-testid="item-gst-select"><SelectValue placeholder="Select GST%" /></SelectTrigger>
                <SelectContent>
                  {gstRates.map(r => {
                    const num = parseFloat(r);
                    return <SelectItem key={r} value={String(num)}>{r}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Shelf Life (days)</Label>
              <Input type="number" value={formData.shelf_life_days} onChange={e => setFormData(p => ({ ...p, shelf_life_days: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Storage Condition</Label>
              <Select value={formData.storage_condition} onValueChange={v => setFormData(p => ({ ...p, storage_condition: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{storageConditions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {/* 1E: Reorder labels */}
            <div>
              <Label>Reorder Level (in {formData.primary_uom || "units"})</Label>
              <Input type="number" value={formData.reorder_level} onChange={e => setFormData(p => ({ ...p, reorder_level: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Reorder Qty (in {formData.primary_uom || "units"})</Label>
              <Input type="number" value={formData.reorder_qty} onChange={e => setFormData(p => ({ ...p, reorder_qty: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Lead Time (days)</Label>
              <Input type="number" value={formData.lead_time_days} onChange={e => setFormData(p => ({ ...p, lead_time_days: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Batch Number Convention (reference note)</Label>
              <Input value={formData.batch_convention_note || ""} onChange={e => setFormData(p => ({ ...p, batch_convention_note: e.target.value }))} placeholder="e.g. Use SKDM- prefix for Dmart" maxLength={50} data-testid="item-batch-convention" />
            </div>

            {/* 5A: Dispatch Pack Size */}
            {showDispatch && (
              <div className="col-span-2">
                <Label>Dispatch Pack Size (boxes per carton)</Label>
                <Input type="number" value={formData.dispatch_pack_size} onChange={e => setFormData(p => ({ ...p, dispatch_pack_size: parseInt(e.target.value) || 0 }))} data-testid="item-dispatch-pack" />
                <p className="text-[10px] text-zinc-400 mt-1">Example: If 10 boxes go into 1 carton for dispatch, enter 10. System uses this to calculate cartons at dispatch time. Stock is always tracked in boxes.</p>
              </div>
            )}

            {/* 4A: Pack Recipe visual table */}
            {needsPackRecipe && (
              <div className="col-span-2">
                <Label>Pack Recipe</Label>
                <div className="border rounded p-2 space-y-1">
                  {(formData.pack_recipe || []).map((r, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input value={r.flavour_or_item || ""} onChange={e => { const pr = [...(formData.pack_recipe || [])]; pr[i] = { ...pr[i], flavour_or_item: e.target.value }; setFormData(p => ({ ...p, pack_recipe: pr })); }} placeholder="Item / Flavour" className="flex-1 h-8 text-xs" />
                      <Input type="number" value={r.qty_per_pack || 0} onChange={e => { const pr = [...(formData.pack_recipe || [])]; pr[i] = { ...pr[i], qty_per_pack: parseInt(e.target.value) || 0 }; setFormData(p => ({ ...p, pack_recipe: pr })); }} className="w-24 h-8 text-xs" placeholder="Qty" />
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { const pr = (formData.pack_recipe || []).filter((_, j) => j !== i); setFormData(p => ({ ...p, pack_recipe: pr })); }}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setFormData(p => ({ ...p, pack_recipe: [...(p.pack_recipe || []), { flavour_or_item: "", qty_per_pack: 0 }] }))}><Plus className="w-3 h-3 mr-1" />Add Row</Button>
                </div>
              </div>
            )}

            {/* 1F: Purchase Pricing */}
            {canViewPricing && (
              <>
                <div className="col-span-2 border-t pt-3">
                  <h3 className="text-sm font-semibold text-zinc-600 mb-2">Purchase Pricing</h3>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Freight Borne By</Label>
                  <div className="flex gap-4 mt-1">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name="freight" value="vendor" checked={formData.freight_borne_by === "vendor"} onChange={() => setFormData(p => ({ ...p, freight_borne_by: "vendor" }))} />
                      Vendor pays freight
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name="freight" value="skippi" checked={formData.freight_borne_by === "skippi"} onChange={() => setFormData(p => ({ ...p, freight_borne_by: "skippi" }))} />
                      Skippi pays freight
                    </label>
                  </div>
                </div>
                <div>
                  <Label>Base Price per {formData.primary_uom || "unit"} (Rs, excl. GST)</Label>
                  <Input type="number" step="0.01" value={formData.base_price} onChange={e => setFormData(p => ({ ...p, base_price: parseFloat(e.target.value) || 0 }))} data-testid="item-base-price" />
                </div>
                <div>
                  <Label>Purchase Price per {formData.primary_uom || "unit"} (auto)</Label>
                  <div className="h-9 flex items-center text-sm font-mono bg-zinc-50 border rounded px-3">Rs {calcPurchasePrice().toFixed(2)}</div>
                </div>
                {formData.freight_borne_by === "skippi" && (
                  <div>
                    <Label>Estimated Freight per {formData.primary_uom || "unit"} (Rs)</Label>
                    <Input type="number" step="0.01" value={formData.estimated_freight} onChange={e => setFormData(p => ({ ...p, estimated_freight: parseFloat(e.target.value) || 0 }))} data-testid="item-est-freight" />
                  </div>
                )}
                <div>
                  <Label>Landed Cost per {formData.primary_uom || "unit"} (auto)</Label>
                  <div className="h-9 flex items-center text-sm font-mono bg-zinc-50 border rounded px-3 font-semibold">Rs {calcLandedCost().toFixed(2)}</div>
                </div>
              </>
            )}

            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea spellCheck={true} autoCorrect="on" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} rows={2} />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea spellCheck={true} autoCorrect="on" value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-item-btn">{saving ? "Saving..." : editId ? "Update Item" : "Save Draft"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Detail Dialog */}
      <Dialog open={!!viewItem} onOpenChange={() => setViewItem(null)}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="item-detail-dialog">
          <DialogHeader><DialogTitle>Item Detail — {viewItem?.item_code}</DialogTitle></DialogHeader>
          {viewItem && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <Detail label="Name" value={viewItem.item_name} />
                <Detail label="Type" value={viewItem.item_type} />
                <Detail label="Category" value={viewItem.item_category} />
                <Detail label="Brand" value={viewItem.brand} />
                <Detail label="MFG Model" value={viewItem.manufacturing_model} />
                <Detail label="Primary UOM" value={viewItem.primary_uom} />
                <Detail label="HSN" value={viewItem.hsn_code} />
                <Detail label="GST" value={`${viewItem.gst_pct}%`} />
                <Detail label="Shelf Life" value={`${viewItem.shelf_life_days} days`} />
                <Detail label="Storage" value={viewItem.storage_condition} />
                <Detail label="Reorder Level" value={viewItem.reorder_level} />
                <Detail label="Lead Time" value={`${viewItem.lead_time_days} days`} />
                {viewItem.dispatch_pack_size > 0 && <Detail label="Dispatch Pack Size" value={`${viewItem.dispatch_pack_size} per carton`} />}
                <Detail label="Status" value={<span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[viewItem.status] || ""}`}>{viewItem.is_deleted ? "inactive" : viewItem.status}</span>} />
                <Detail label="Created By" value={viewItem.created_by_name} />
                {viewItem.approved_by && <Detail label="Approved By" value={viewItem.approved_by} />}
                <Detail label="Last Amended" value={viewItem.last_amended_at ? `by ${viewItem.last_amended_by || "—"} on ${fmtIST(viewItem.last_amended_at)}` : "Not amended yet"} />
                {viewItem.rejection_reason && <Detail label="Rejection Reason" value={viewItem.rejection_reason} />}
                {(viewItem.batch_convention_note || viewItem.batch_number_format) && <Detail label="Batch Convention" value={viewItem.batch_convention_note || viewItem.batch_number_format} />}
              </div>
              {/* Pricing section */}
              {canViewPricing && viewItem.landed_cost_paise > 0 && (
                <div className="border-t pt-2">
                  <h4 className="text-xs font-semibold text-zinc-500 mb-1">Purchase Pricing</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Detail label="Base Price" value={`Rs ${((viewItem.base_price_paise || 0) / 100).toFixed(2)} per ${viewItem.primary_uom}`} />
                    <Detail label="Purchase Price" value={`Rs ${((viewItem.purchase_price_paise || 0) / 100).toFixed(2)}`} />
                    <Detail label="Freight By" value={viewItem.freight_borne_by} />
                    {viewItem.freight_borne_by === "skippi" && <Detail label="Est. Freight" value={`Rs ${((viewItem.estimated_freight_paise || 0) / 100).toFixed(2)}`} />}
                    <Detail label="Landed Cost" value={<span className="font-semibold">Rs {((viewItem.landed_cost_paise || 0) / 100).toFixed(2)} per {viewItem.primary_uom}</span>} />
                    {(viewItem.actual_freight_paise || 0) > 0 && <Detail label="Actual Freight" value={`Rs ${((viewItem.actual_freight_paise || 0) / 100).toFixed(2)}`} />}
                  </div>
                </div>
              )}
              {viewItem.description && <Detail label="Description" value={viewItem.description} />}
              {viewItem.notes && <Detail label="Notes" value={viewItem.notes} />}
              <div className="flex gap-2 pt-3 border-t flex-wrap">
                {viewItem.status === "draft" && admin && (
                  <>
                    <Button size="sm" onClick={() => handleApprove(viewItem._id)} data-testid="approve-item-btn"><Check className="w-3 h-3 mr-1" />Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => { setRejectId(viewItem._id); }} data-testid="reject-item-btn"><X className="w-3 h-3 mr-1" />Reject</Button>
                  </>
                )}
                {admin && viewItem.is_deleted && (
                  <Button size="sm" onClick={() => handleReactivate(viewItem._id)} data-testid="reactivate-item-btn"><Power className="w-3 h-3 mr-1" />Reactivate</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Reason Dialog */}
      <Dialog open={!!rejectId} onOpenChange={() => { setRejectId(null); setRejectReason(""); }}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} data-testid="reject-item-dialog">
          <DialogHeader><DialogTitle>Reject Item</DialogTitle></DialogHeader>
          <Label>Reason *</Label>
          <Textarea spellCheck={true} autoCorrect="on" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Provide rejection reason..." data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-btn">Reject Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={!!deactivateItem} onOpenChange={() => setDeactivateItem(null)}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} data-testid="deactivate-item-dialog">
          <DialogHeader><DialogTitle>Deactivate Item</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">
            Are you sure you want to deactivate <strong>{deactivateItem?.item_name}</strong>?
            This item will no longer be available for new BOMs or purchase orders. Existing records are unaffected.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateItem(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeactivate} data-testid="confirm-deactivate-btn">Deactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <span className="text-zinc-500 text-xs">{label}</span>
      <div className="font-medium">{value || "—"}</div>
    </div>
  );
}
